/**
 * Early Access Modal Component
 *
 * Modal that allows users to enable Early Access features directly from disabled buttons.
 */

import { useCallback, type ReactElement } from 'react';
import { usePrompt } from '@studio/design-system/prompt';
import { useEarlyAccess, EARLY_ACCESS_EVENTS, type EarlyAccessFeature } from '#~/posthog/index.js';
import { usePostHog } from '#~/posthog/hooks/use-posthog.js';

/** Modal content configuration for each feature */
const EARLY_ACCESS_CONTENT: Record<
  EarlyAccessFeature,
  {
    title: string;
    body: ReactElement;
  }
> = {
  recording: {
    title: 'Enable Recording (Early Access)',
    body: (
      <div className='text-sm'>
        <p className='mb-12'>
          Recording is currently in <strong>early beta</strong>. This includes:
        </p>
        <ul className='mb-12 ml-[5rem] list-inside list-disc text-left'>
          <li>ISO Recording for all video sources</li>
        </ul>
        <p className='mb-12'>
          As a beta feature, you may experience occasional issues. You can disable this feature at
          any time.
        </p>
      </div>
    )
  },
  output: {
    title: 'Enable Output (Early Access)',
    body: (
      <div className='text-sm'>
        <p className='mb-12'>
          Output is currently in <strong>early beta</strong>. Enabling it will unlock:
        </p>
        <ul className='mb-12 ml-48 list-inside list-disc text-left'>
          <li>NDI Output</li>
          <li>RTMP Streaming</li>
        </ul>
        <p className='mb-12'>
          As a beta feature, you may experience occasional issues. You can disable this feature at
          any time.
        </p>
      </div>
    )
  }
};

export interface UseEarlyAccessModalReturn {
  /** Open the early access modal for the specified feature */
  open: () => void;
  /** Whether the early access feature is enabled */
  isEnabled: boolean;
  /** Whether the status is loading */
  loading: boolean;
  /** Enable early access for this feature */
  enable: () => Promise<void>;
}

/**
 * Hook that provides a function to open the early access modal.
 *
 * @param feature - The early access feature type ('recording' or 'output')
 * @returns Object with open function and current status
 *
 * @example
 * ```tsx
 * const { open, isEnabled } = useEarlyAccessModal('recording');
 *
 * // Show modal when user clicks disabled button
 * <Button disabled={!isEnabled} onClick={isEnabled ? startRecording : open}>
 *   Start Recording
 * </Button>
 * ```
 */
export function useEarlyAccessModal(feature: EarlyAccessFeature): UseEarlyAccessModalReturn {
  const prompt = usePrompt();
  const { isEnabled, loading, enable } = useEarlyAccess(feature);
  const { capture } = usePostHog();
  const content = EARLY_ACCESS_CONTENT[feature];

  const open = useCallback(() => {
    const clickEvent =
      feature === 'recording'
        ? EARLY_ACCESS_EVENTS.CLICKED_RECORDING_BUTTON
        : EARLY_ACCESS_EVENTS.CLICKED_OUTPUT_BUTTON;
    capture(clickEvent, { early_access_enabled: isEnabled });

    prompt.confirm({
      title: content.title,
      className: 'min-w-[450px]',
      message: content.body,
      okay: {
        label: 'Enable Early Access',
        variant: 'primary'
      },
      cancel: {
        label: 'Cancel'
      },
      onConfirm: async (answer: boolean) => {
        if (answer) {
          try {
            await enable();
          } catch (error) {
            console.error(`Failed to enable early access for ${feature}:`, error);
          }
        }
      }
    });
  }, [feature, isEnabled, prompt, content, capture, enable]);

  return {
    open,
    isEnabled,
    loading,
    enable
  };
}
